import _ from 'lodash'
import { round } from 'mathjs'

import { InventoryStockTypes, StockPriceTypes, CustomerOverrideTypes, CustomerOverrideLevelTypes, ApprovalTypes, PriceListDataSourceTypes } from '@/store/types'

export default {
  data() {
    return {
      stockPriceGroupList: [],
      stockCategoryList: [],
      salesRateList: [],

      defaultSalesRate: null,

      priceListRootData: {},

      customerPriceList: {
        priceData: {
          priceSettings: [],
          stockPriceData: []
        }
      },

      loadingError: ''
    }
  },

  computed: {
    glassStockCategoryList() {
      return this.$data.stockCategoryList.filter(stockCategory => stockCategory.isActive && stockCategory.stockType == InventoryStockTypes.glass)
    },
    serviceStockCategoryList() {
      return this.$data.stockCategoryList.filter(stockCategory => stockCategory.isActive && stockCategory.stockType == InventoryStockTypes.glassService)
    },
    generalStockCategoryList() {
      return this.$data.stockCategoryList.filter(stockCategory => stockCategory.isActive && stockCategory.stockType == InventoryStockTypes.general)
    },
    assemblyStockCategoryList() {
      return this.$data.stockCategoryList.filter(stockCategory => stockCategory.isActive && stockCategory.stockType == InventoryStockTypes.assembly)
    },
    processedPriceGroupList() {
      return this.$data.stockPriceGroupList.filter(priceGroup => priceGroup.isActive)
    },
    servicesPriceGroupList() {
      return this.$data.stockPriceGroupList.filter(priceGroup => priceGroup.isActive && priceGroup.servicePriceRanges.length > 0)
    },

  },
  methods: {
    // Request the price list data & associated pricing data
    // Returns a promise resolved on successful load
    requestCustomerPriceListData(customerId, priceListId, priceListDataSource) {
      return new Promise((resolve, reject) => {
        this.loadingError = ''

        // Store category expanded states & restore those after loading
        var curCategoryList = _.cloneDeep(this.$data.stockCategoryList)

        var promiseList = [
          this.$apiService.pricingService.getSalesRateList(),
          this.$apiService.pricingService.getStockPriceGroupList(),
          this.$apiService.inventoryService.getStockCategoryList()
        ]
        if(customerId)
          promiseList.push(this.$apiService.contactService.getContact(customerId))
        
        Promise.all(promiseList)
        .then(results => {
          this.$data.salesRateList = results[0].data
          this.$data.stockPriceGroupList = results[1].data
          this.$data.stockCategoryList = results[2].data.map(category => {
            var curCategory = curCategoryList.find(cat => cat.id == category.id)

            return {
              id: category.id,
              name: category.name,
              isActive: category.isActive,
              stockType: category.stockType,
              path: category.path,
              editing: false,
              submitting: false,
              expanded: curCategory ? curCategory.expanded : false
            }
          })

          this.$data.defaultSalesRate = this.$data.salesRateList.filter(rate => rate.isDefault)[0]

          // Get default sales rate for the customer (if set)
          if(customerId) {
            let contactData = results[3].data
            if(contactData.defaultSalesRateId) {
              let customerDefaultRate = this.$data.salesRateList.filter(rate => rate.id == contactData.defaultSalesRateId)[0]
              if(customerDefaultRate)
                this.$data.defaultSalesRate = customerDefaultRate
            }
          }

          // Load the customer price list (if ID set, and not a new price list)
          if(customerId && priceListId) {
            // Load an existing price list, but override the data source type (used when editing an existing price list)
            var params = {}
            if(priceListDataSource)
              params.dataSourceType = priceListDataSource

            this.$apiService.pricingService.getCustomerPriceList(customerId, priceListId, params)
            .then(response => {
              this.$data.customerPriceList = response.data

              // Add in properties for editing
              this.$data.customerPriceList.priceData.stockPriceData.forEach(stock => {
                stock.priceData.forEach(priceData => {
                  this.$set(priceData, 'editingPrice', priceData.price)
                  if(priceData.isVariation)
                    this.$set(priceData, 'originalPrice', priceData.price)
                })
              })

              // Prepare the stock list in a structure better for the UI display & relying less on lookup functions
              // StockType -> Categories -> Stock -> PriceDataList (service ranges, price groups)
              this.priceListRootData = this.buildStockCustomerPriceListData(response.data, this.$data.stockCategoryList)

            })
            .finally(() => {
              return resolve()
            })
          } else {
            // Get default price list settings for a customer
            // For new price lists, set how the pricing data is to be sourced
            var priceListPromise = null
            if(customerId)
              priceListPromise = this.$apiService.pricingService.getCustomerDefaultPriceList(customerId, { dataSourceType: priceListDataSource })
            else
              priceListPromise = this.$apiService.pricingService.getSystemDefaultPriceList({ dataSourceType: priceListDataSource })

            priceListPromise.then(response => {
              this.$data.customerPriceList = response.data

              // Add in properties for editing
              this.$data.customerPriceList.priceData.stockPriceData.forEach(stock => {
                stock.priceData.forEach(priceData => {
                  this.$set(priceData, 'editingPrice', priceData.price)
                })
              })

              // Prepare the stock list in a structure better for the UI display & relying less on lookup functions
              // StockType -> Categories -> Stock -> PriceDataList (service ranges, price groups)
              this.priceListRootData = this.buildStockCustomerPriceListData(response.data, this.$data.stockCategoryList)
            })
            .finally(() => {
              return resolve()
            })
          }
        })
        .catch(err => {
          this.loadingError = 'Failed to load pricing data'
          return reject(err)
        })
      })
    },

    // Display
    categoryTitle(category) {        
      var seperator = '<i class="fas fa-caret-right"></i>'

      var arrow = category.expanded ? 'fa-angle-down' : 'fa-angle-right'
      var arrowIcon = `<i class="far ${arrow}"> </i>`

      var pathInfo = category.path.map(c => c.name).join(` ${seperator} `)
      if(category.path.length > 0)
        pathInfo += ` ${seperator}`


      return `&nbsp; ${arrowIcon} ${pathInfo} ${category.name}`
    },
    categoryBarClicked(evt, category, nameColumn) {
      // Ignore clicks on permission switches
      if($(evt.target).is(':input') || $(evt.target).is('label'))
        return true

      category.expanded = !category.expanded
      return true
    },
    getStockListForCategory(category) {
      return this.$data.customerPriceList.priceData.stockPriceData.filter(stock => stock.stockCategoryId == category.id)
    },
    isCategoryEmpty(category) {
      return this.getStockListForCategory(category).length == 0
    },

    getCategoryStockListContainsPending(stockCategory, stockPriceGroupId = null, serviceRangeId = null) {
      let stockList = this.$data.customerPriceList.priceData.stockPriceData.filter(stock => stock.stockCategoryId == stockCategory.id)

      for(var stock of stockList) {
        var priceData = stock.priceData.find(priceData => {
          if(stockPriceGroupId != null && stockPriceGroupId != priceData.stockPriceGroupId)
            return false
          if(serviceRangeId != null && serviceRangeId != priceData.servicePriceRangeId)
            return false
          return true
        })
        if(!priceData)
          continue
        if(priceData.isSalesPendingUpdate)
          return true
      }

      return false
    },

    restoreStockCategoryRate(stockCategory) {
      if(stockCategory.stockType == InventoryStockTypes.glass) {
        
        this.processedPriceGroupList.forEach(priceGroup => {
          var customerPrice = this.customerPriceList.priceData.priceSettings.find(price => {
            return price.priceType == StockPriceTypes.processedPriceGroup &&
            price.stockCategoryId == stockCategory.id &&
            price.stockPriceGroupId == priceGroup.id &&
            price.overrideType == CustomerOverrideTypes.rate &&
            price.overrideLevelType == CustomerOverrideLevelTypes.category
          })
          
          if(customerPrice)
            this.setProcessedCategoryRate(stockCategory, priceGroup.id, customerPrice.originalSalesRateId)
        })
      }
      if(stockCategory.stockType == InventoryStockTypes.glassService) {

        // Restore for ALL price groups
        this.servicesPriceGroupList.forEach(priceGroup => {
          priceGroup.servicePriceRanges.forEach(serviceRange => {
            var customerPrice = this.customerPriceList.priceData.priceSettings.find(price => {
              return price.priceType == StockPriceTypes.servicePriceRange &&
              price.stockCategoryId == stockCategory.id &&
              price.stockPriceGroupId == priceGroup.id &&
              price.servicePriceRangeId == serviceRange.id &&
              price.overrideType == CustomerOverrideTypes.rate &&
              price.overrideLevelType == CustomerOverrideLevelTypes.category
            })
            
            if(customerPrice)
              this.setServiceRangeCategoryRate(stockCategory, priceGroup.id, serviceRange.id, customerPrice.originalSalesRateId)
          })
        })
      }
      if(stockCategory.stockType == InventoryStockTypes.general) {
        var customerPrice = this.customerPriceList.priceData.priceSettings.find(price => {
          return price.priceType == StockPriceTypes.general &&
          price.stockCategoryId == stockCategory.id &&
          price.overrideType == CustomerOverrideTypes.rate &&
          price.overrideLevelType == CustomerOverrideLevelTypes.category
        })

        if(customerPrice)
          this.setGeneralCategoryRate(stockCategory, customerPrice.originalSalesRateId)
      }
      if(stockCategory.stockType == InventoryStockTypes.assembly) {
        var customerPrice = this.customerPriceList.priceData.priceSettings.find(price => {
          return price.priceType == StockPriceTypes.assembly &&
          price.stockCategoryId == stockCategory.id &&
          price.overrideType == CustomerOverrideTypes.rate &&
          price.overrideLevelType == CustomerOverrideLevelTypes.category
        })

        if(customerPrice)
          this.setAssemblyCategoryRate(stockCategory, customerPrice.originalSalesRateId)
      }
    },

    // Apply a sales rate to a category
    applySalesRateToCategory(stockCategoryId, salesRateId, priceGroupId, servicePriceRangeId) {
      let stockCategory = this.$data.stockCategoryList.find(cat => cat.id == stockCategoryId)

      if(stockCategory) {
        if(stockCategory.stockType == InventoryStockTypes.glass)
          this.setProcessedCategoryRate(stockCategory, priceGroupId, salesRateId)
        else if(stockCategory.stockType == InventoryStockTypes.glassService)
          this.setServiceRangeCategoryRate(stockCategory, priceGroupId, servicePriceRangeId, salesRateId)
        else if(stockCategory.stockType == InventoryStockTypes.general)
          this.setGeneralCategoryRate(stockCategory, salesRateId)
        else if(stockCategory.stockType == InventoryStockTypes.assembly)
          this.setAssemblyCategoryRate(stockCategory, salesRateId)
      }
    },

    // Processed Glass
    getProcessedCategoryRate(stockCategory, priceGroupId) {
      var customerPrice = this.customerPriceList.priceData.priceSettings.find(price => {
        return price.priceType == StockPriceTypes.processedPriceGroup &&
        price.stockCategoryId == stockCategory.id &&
        price.stockPriceGroupId == priceGroupId &&
        price.overrideType == CustomerOverrideTypes.rate &&
        price.overrideLevelType == CustomerOverrideLevelTypes.category
      })
      if(customerPrice == null) {
        return {
          id: this.$data.defaultSalesRate ? this.$data.defaultSalesRate.id : 0,
          name: this.$data.defaultSalesRate ? this.$data.defaultSalesRate.name : '-',
          code: this.$data.defaultSalesRate ? this.$data.defaultSalesRate.code : '-'
        }
      } else {
        let salesRate = this.$data.salesRateList.find(rate => rate.id == customerPrice.salesRateId)
        return {
          id: customerPrice.salesRateId,
          code: salesRate ? salesRate.code : '',
          name: salesRate ? salesRate.name : '',
          prevRateName: customerPrice.activeSalesRate ? customerPrice.activeSalesRate.name : ''
        }
      }
    },

    processedCategoryRateContainsRateVariation(stockCategory, priceGroupId) {
      return stockCategory.stockList.filter(stock => {
        if(stock.priceGroups[priceGroupId])
          return stock.priceGroups[priceGroupId].isRateVariation && !stock.priceGroups[priceGroupId].isVariation
        return false
      }).length > 0
    },
    processedCategoryRateContainsVariation(stockCategory, priceGroupId) {
      return stockCategory.stockList.filter(stock => {
        if(stock.priceGroups[priceGroupId])
          return stock.priceGroups[priceGroupId].isVariation
        return false
      }).length > 0
    },
    processedCategoryRateContainsRateChange(stockCategory, priceGroupId) {
      var variationPrice = this.customerPriceList.priceData.priceSettings.find(price => {
        return price.priceType == StockPriceTypes.processedPriceGroup &&
        price.stockCategoryId == stockCategory.id &&
        price.stockPriceGroupId == priceGroupId &&
        price.overrideType == CustomerOverrideTypes.rate &&
        price.overrideLevelType == CustomerOverrideLevelTypes.category
      })
      if(variationPrice && variationPrice.activeSalesRate) {
        return variationPrice.activeSalesRate.id != variationPrice.salesRate.id
      }
      return false
    },

    setProcessedCategoryRate(stockCategory, priceGroupId, salesRateId) {
      let salesRate = this.$data.salesRateList.find(rate => rate.id == salesRateId)
      if(!salesRate)
        return

      var customerPrice = this.customerPriceList.priceData.priceSettings.find(price => {
        return price.priceType == StockPriceTypes.processedPriceGroup &&
        price.stockCategoryId == stockCategory.id &&
        price.stockPriceGroupId == priceGroupId &&
        price.overrideType == CustomerOverrideTypes.rate &&
        price.overrideLevelType == CustomerOverrideLevelTypes.category
      })
      if(customerPrice == null) {
        this.customerPriceList.priceData.priceSettings.push({
          priceType: StockPriceTypes.processedPriceGroup,
         stockCategoryId: stockCategory.id,
         stockPriceGroupId: priceGroupId,
          overrideType: CustomerOverrideTypes.rate,
          overrideLevelType: CustomerOverrideLevelTypes.category,
          originalSalesRateId: this.$data.defaultSalesRate ? this.$data.defaultSalesRate.id : 0,
         salesRateId: salesRateId,
          salesRate: salesRate
        })
      } else {
        let defaultSalesRateId = this.$data.defaultSalesRate ? this.$data.defaultSalesRate.id : 0
        if(!customerPrice.originalSalesRateId)
          customerPrice.originalSalesRateId = customerPrice.salesRateId || defaultSalesRateId

        customerPrice.salesRateId = salesRateId
        customerPrice.salesRate = salesRate
      }

      // Update stock details
      let adjustRateFraction = round(parseFloat(salesRate.percentAdjust) / 100.0, 2)

      this.priceListRootData.glass.forEach(category => {
        if(category.id != stockCategory.id)
          return

        category.stockList.forEach(stock => {
          var priceData = stock.priceGroups[priceGroupId]
          if(priceData.price !== null && priceData.price !== undefined) {
            
            priceData.salesRateId = salesRate.id

            priceData.calculatedPrice = round(priceData.salesPrice + (priceData.salesPrice * adjustRateFraction), 2)
            
            // JB: 13/05/2021 - We are now wiping out variations if the category rate was changed as requested by KC
            priceData.isRateVariation = false
            priceData.isVariation = false
            priceData.price = priceData.calculatedPrice
          }
        })        
      })
    },


    // Glass Service Type
    getServiceRangeCategoryRate(stockCategory, priceGroupId, serviceRangeId) {
      var customerPrice = this.customerPriceList.priceData.priceSettings.find(price => {
        return price.priceType == StockPriceTypes.servicePriceRange &&
        price.stockCategoryId == stockCategory.id &&
        price.stockPriceGroupId == priceGroupId &&
        price.servicePriceRangeId == serviceRangeId &&
        price.overrideType == CustomerOverrideTypes.rate &&
        price.overrideLevelType == CustomerOverrideLevelTypes.category
      })
      if(customerPrice == null) {
        return {
          id: this.$data.defaultSalesRate ? this.$data.defaultSalesRate.id : 0,
          name: this.$data.defaultSalesRate ? this.$data.defaultSalesRate.name : '-',
          code: this.$data.defaultSalesRate ? this.$data.defaultSalesRate.code : '-'
        }
      } else {
        let salesRate = this.$data.salesRateList.find(rate => rate.id == customerPrice.salesRateId)
        return {
          id: customerPrice.salesRateId,
          code: salesRate ? salesRate.code : '',
          name: salesRate ? salesRate.name : '',
          prevRateName: customerPrice.activeSalesRate ? customerPrice.activeSalesRate.name : ''
        }
      }
    },
    serviceRangeCategoryRateContainsRateVariation(stockCategory, priceGroupId, serviceRangeId) {
      return stockCategory.stockList.filter(stock => {
        if(stock.priceGroups[priceGroupId][serviceRangeId])
          return stock.priceGroups[priceGroupId][serviceRangeId].isRateVariation && !stock.priceGroups[priceGroupId][serviceRangeId].isVariation
        return false
      }).length > 0
    },
    serviceRangeCategoryRateContainsVariation(stockCategory, priceGroupId, serviceRangeId) {
      return stockCategory.stockList.filter(stock => {
        if(stock.priceGroups[priceGroupId][serviceRangeId])
          return stock.priceGroups[priceGroupId][serviceRangeId].isVariation
        return false
      }).length > 0
    },
    serviceRangeCategoryRateContainsRateChange(stockCategory, priceGroupId, serviceRangeId) {
      var variationPrice = this.customerPriceList.priceData.priceSettings.find(price => {
        return price.priceType == StockPriceTypes.servicePriceRange &&
        price.stockCategoryId == stockCategory.id &&
        price.stockPriceGroupId == priceGroupId &&
        price.servicePriceRangeId == serviceRangeId &&
        price.overrideType == CustomerOverrideTypes.rate &&
        price.overrideLevelType == CustomerOverrideLevelTypes.category
      })
      if(variationPrice && variationPrice.activeSalesRate) {
        return variationPrice.activeSalesRate.id != variationPrice.salesRate.id
      }
      return false
    },

    setServiceRangeCategoryRate(stockCategory, priceGroupId, serviceRangeId, salesRateId) {
      let salesRate = this.$data.salesRateList.find(rate => rate.id == salesRateId)
      if(!salesRate)
        return

      var customerPrice = this.customerPriceList.priceData.priceSettings.find(price => {
        return price.priceType == StockPriceTypes.servicePriceRange &&
        price.stockCategoryId == stockCategory.id &&
        price.stockPriceGroupId == priceGroupId &&
        price.servicePriceRangeId == serviceRangeId &&
        price.overrideType == CustomerOverrideTypes.rate &&
        price.overrideLevelType == CustomerOverrideLevelTypes.category
      })
      if(customerPrice == null) {
        this.customerPriceList.priceData.priceSettings.push({
          priceType: StockPriceTypes.servicePriceRange,
         stockCategoryId: stockCategory.id,
         stockPriceGroupId: priceGroupId,
         servicePriceRangeId: serviceRangeId,
          overrideType: CustomerOverrideTypes.rate,
          overrideLevelType: CustomerOverrideLevelTypes.category,
          originalSalesRateId: this.$data.defaultSalesRate ? this.$data.defaultSalesRate.id : 0,
         salesRateId: salesRateId,
          salesRate: salesRate
        })
      } else {
        let defaultSalesRateId = this.$data.defaultSalesRate ? this.$data.defaultSalesRate.id : 0
        if(!customerPrice.originalSalesRateId)
          customerPrice.originalSalesRateId = customerPrice.salesRateId || defaultSalesRateId

        customerPrice.salesRateId = salesRateId
        customerPrice.salesRate = salesRate
      }

      // Update calculated prices
      let adjustRateFraction = round(parseFloat(salesRate.percentAdjust) / 100.0, 2)
      this.priceListRootData.glassServices.forEach(category => {
        if(category.id != stockCategory.id)
          return

          category.stockList.forEach(stock => {
            var priceData = stock.priceGroups[priceGroupId][serviceRangeId]
            if(priceData.price !== null && priceData.price !== undefined) {
              
              priceData.salesRateId = salesRate.id

              priceData.calculatedPrice = round(priceData.salesPrice + (priceData.salesPrice * adjustRateFraction), 2)
              
              // JB: 13/05/2021 - We are now wiping out variations if the category rate was changed as requested by KC
              priceData.isRateVariation = false
              priceData.isVariation = false
              priceData.price = priceData.calculatedPrice
            }
          })        
      })
    },
    serviceStockVariationListForPriceGroup(priceGroupId) {
      return this.$data.customerPriceList.priceData.priceSettings.filter(price => {
        return price.priceType == StockPriceTypes.servicePriceRange &&
        price.stockPriceGroupId == priceGroupId &&
        price.overrideLevelType == CustomerOverrideLevelTypes.stock
      })
    },

    // General
    getGeneralCategoryRate(stockCategory) {
      var customerPrice = this.customerPriceList.priceData.priceSettings.find(price => {
        return price.priceType == StockPriceTypes.general &&
        price.stockCategoryId == stockCategory.id &&
        price.overrideType == CustomerOverrideTypes.rate &&
        price.overrideLevelType == CustomerOverrideLevelTypes.category
      })
      if(customerPrice == null) {
        return {
          id: this.$data.defaultSalesRate ? this.$data.defaultSalesRate.id : 0,
          name: this.$data.defaultSalesRate ? this.$data.defaultSalesRate.name : '-',
          code: this.$data.defaultSalesRate ? this.$data.defaultSalesRate.code : '-'
        }
      } else {
        let salesRate = this.$data.salesRateList.find(rate => rate.id == customerPrice.salesRateId)
        return {
          id: customerPrice.salesRateId,
          code: salesRate ? salesRate.code : '',
          name: salesRate ? salesRate.name : '',
          prevRateName: customerPrice.activeSalesRate ? customerPrice.activeSalesRate.name : ''
        }
      }
    },
    generalCategoryRateContainsRateVariation(stockCategory) {
      return stockCategory.stockList.filter(stock => {
        if(stock.priceData)
          return stock.priceData.isRateVariation && !stock.priceData.isVariation
        return false
      }).length > 0
    },
    generalCategoryRateContainsVariation(stockCategory) {
      return stockCategory.stockList.filter(stock => {
        if(stock.priceData)
          return stock.priceData.isVariation
        return false
      }).length > 0
    },
    generalCategoryRateContainsRateChange(stockCategory) {
      var variationPrice = this.customerPriceList.priceData.priceSettings.find(price => {
        return price.priceType == StockPriceTypes.general &&
        price.stockCategoryId == stockCategory.id &&
        price.overrideType == CustomerOverrideTypes.rate &&
        price.overrideLevelType == CustomerOverrideLevelTypes.category
      })

      if(variationPrice && variationPrice.activeSalesRate) {
        return variationPrice.activeSalesRate.id != variationPrice.salesRate.id
      }
      return false
    },

    setGeneralCategoryRate(stockCategory, salesRateId) {
      let salesRate = this.$data.salesRateList.find(rate => rate.id == salesRateId)
      if(!salesRate)
        return

      var customerPrice = this.customerPriceList.priceData.priceSettings.find(price => {
        return price.priceType == StockPriceTypes.general &&
        price.stockCategoryId == stockCategory.id &&
        price.overrideType == CustomerOverrideTypes.rate &&
        price.overrideLevelType == CustomerOverrideLevelTypes.category
      })
      if(customerPrice == null) {
        this.customerPriceList.priceData.priceSettings.push({
          priceType: StockPriceTypes.general,
         stockCategoryId: stockCategory.id,
          overrideType: CustomerOverrideTypes.rate,
          overrideLevelType: CustomerOverrideLevelTypes.category,
          originalSalesRateId: this.$data.defaultSalesRate ? this.$data.defaultSalesRate.id : 0,
         salesRateId: salesRateId,
          salesRate: salesRate
        })
      } else {
        let defaultSalesRateId = this.$data.defaultSalesRate ? this.$data.defaultSalesRate.id : 0
        if(!customerPrice.originalSalesRateId)
          customerPrice.originalSalesRateId = customerPrice.salesRateId || defaultSalesRateId

        customerPrice.salesRateId = salesRateId
        customerPrice.salesRate = salesRate
      }

      // Update calculated prices
      let adjustRateFraction = round(parseFloat(salesRate.percentAdjust) / 100.0, 2)

      this.priceListRootData.general.forEach(category => {
        if(category.id != stockCategory.id)
          return

        category.stockList.forEach(stock => {
          var priceData = stock.priceData
          if(priceData.price !== null && priceData.price !== undefined) {
            
            priceData.salesRateId = salesRate.id

            priceData.calculatedPrice = round(priceData.salesPrice + (priceData.salesPrice * adjustRateFraction), 2)
            
            // JB: 13/05/2021 - We are now wiping out variations if the category rate was changed as requested by KC
            priceData.isRateVariation = false
            priceData.isVariation = false
            priceData.price = priceData.calculatedPrice
          }
        })        
      })
    },

    // Assembly
    getAssemblyCategoryRate(stockCategory) {
      var customerPrice = this.customerPriceList.priceData.priceSettings.find(price => {
        return price.priceType == StockPriceTypes.assembly &&
        price.stockCategoryId == stockCategory.id &&
        price.overrideType == CustomerOverrideTypes.rate &&
        price.overrideLevelType == CustomerOverrideLevelTypes.category
      })
      if(customerPrice == null) {
        return {
          id: this.$data.defaultSalesRate ? this.$data.defaultSalesRate.id : 0,
          name: this.$data.defaultSalesRate ? this.$data.defaultSalesRate.name : '-',
          code: this.$data.defaultSalesRate ? this.$data.defaultSalesRate.code : '-'
        }
      } else {
        let salesRate = this.$data.salesRateList.find(rate => rate.id == customerPrice.salesRateId)
        return {
          id: customerPrice.salesRateId,
          code: salesRate ? salesRate.code : '',
          name: salesRate ? salesRate.name : '',
          prevRateName: customerPrice.activeSalesRate ? customerPrice.activeSalesRate.name : ''
        }
      }
    },
    assemblyCategoryRateContainsRateVariation(stockCategory) {
      return stockCategory.stockList.filter(stock => {
        if(stock.priceData)
          return stock.priceData.isRateVariation && !stock.priceData.isVariation
        return false
      }).length > 0
    },
    assemblyCategoryRateContainsVariation(stockCategory) {
      return stockCategory.stockList.filter(stock => {
        if(stock.priceData)
          return stock.priceData.isVariation
        return false
      }).length > 0
    },
    assemblyCategoryRateContainsRateChange(stockCategory) {
      var variationPrice = this.customerPriceList.priceData.priceSettings.find(price => {
        return price.priceType == StockPriceTypes.assembly &&
        price.stockCategoryId == stockCategory.id &&
        price.overrideType == CustomerOverrideTypes.rate &&
        price.overrideLevelType == CustomerOverrideLevelTypes.category
      })

      if(variationPrice && variationPrice.activeSalesRate) {
        return variationPrice.activeSalesRate.id != variationPrice.salesRate.id
      }
      return false
    },

    setAssemblyCategoryRate(stockCategory, salesRateId) {
      let salesRate = this.$data.salesRateList.find(rate => rate.id == salesRateId)
      if(!salesRate)
        return

      var customerPrice = this.customerPriceList.priceData.priceSettings.find(price => {
        return price.priceType == StockPriceTypes.assembly &&
        price.stockCategoryId == stockCategory.id &&
        price.overrideType == CustomerOverrideTypes.rate &&
        price.overrideLevelType == CustomerOverrideLevelTypes.category
      })
      if(customerPrice == null) {
        this.customerPriceList.priceData.priceSettings.push({
          priceType: StockPriceTypes.assembly,
          stockCategoryId: stockCategory.id,
          overrideType: CustomerOverrideTypes.rate,
          overrideLevelType: CustomerOverrideLevelTypes.category,
          originalSalesRateId: this.$data.defaultSalesRate ? this.$data.defaultSalesRate.id : 0,
          salesRateId: salesRateId,
          salesRate: salesRate
        })
      } else {
        let defaultSalesRateId = this.$data.defaultSalesRate ? this.$data.defaultSalesRate.id : 0
        if(!customerPrice.originalSalesRateId)
          customerPrice.originalSalesRateId = customerPrice.salesRateId || defaultSalesRateId

        customerPrice.salesRateId = salesRateId
        customerPrice.salesRate = salesRate
      }

      // Update calculated prices
      let adjustRateFraction = round(parseFloat(salesRate.percentAdjust) / 100.0, 2)

      this.priceListRootData.assembly.forEach(category => {
        if(category.id != stockCategory.id)
          return

        category.stockList.forEach(stock => {
          var priceData = stock.priceData
          if(priceData.price !== null && priceData.price !== undefined) {
            
            priceData.salesRateId = salesRate.id

            priceData.calculatedPrice = round(priceData.salesPrice + (priceData.salesPrice * adjustRateFraction), 2)
            
            // JB: 13/05/2021 - We are now wiping out variations if the category rate was changed as requested by KC
            priceData.isRateVariation = false
            priceData.isVariation = false
            priceData.price = priceData.calculatedPrice
          }
        })        
      })
    },

    salesRateIcon(salesRate) {
      let percent = parseFloat(salesRate.percentAdjust)
      if(isNaN(percent))
        return 'fa-exclamation-triangle'

      if(percent == 0)
        return 'fa-minus'
      if(percent > 0)
        return 'fa-caret-up'
      return 'fa-caret-down'
    },

    // Deprecated?
    updateStockPriceWithRate(stock, salesRate, priceGroupId = null, serviceRangeId = null) {
      let adjustRateFraction = round(parseFloat(salesRate.percentAdjust) / 100.0, 2)

      var priceData = this.findPriceData(stock, priceGroupId, serviceRangeId)
      if(!isNaN(parseFloat(priceData.salesPrice))) {
        this.$set(priceData, 'editingPrice', round(priceData.salesPrice + (priceData.salesPrice * adjustRateFraction), 2))
        this.$set(priceData, 'isVariation', true)
        this.$set(priceData, 'isRemoved', false)
      }
    },
    // Deprecated?
    restoreStockPriceVariation(stock, priceGroupId = null, serviceRangeId = null) {
      var priceData = stock.priceData
      if(priceGroupId)
        priceData = stock.priceGroups[priceGroupId]
      if(priceGroupId && serviceRangeId)
        priceData = stock.priceGroups[priceGroupId][serviceRangeId]

      if(priceData.originalPrice)
        priceData.editingPrice = priceData.originalPrice
    },

    hasPriceData(stock, stockPriceGroupId = null, serviceRangeId = null) {
      return this.findPriceData(stock, stockPriceGroupId, serviceRangeId).price !== undefined
    },

    findPriceData(stock, stockPriceGroupId = null, serviceRangeId = null) {
      var priceData = stock.priceData.find(priceData => {
        if(stockPriceGroupId != null && stockPriceGroupId != priceData.stockPriceGroupId)
          return false
        if(serviceRangeId != null && serviceRangeId != priceData.servicePriceRangeId)
          return false
        return true
      })

      return priceData ? priceData : {}
    },
    displayPriceData(stock, stockPriceGroupId = null, serviceRangeId = null) {
      var priceData = this.findPriceData(stock, stockPriceGroupId, serviceRangeId)

      var output = '<span class="'
      if(priceData.isVariation)
        output += 'pricing-list-variation font-weight-bold'
      if(priceData.isSalesPendingUpdate && !this.editing)
        output +='pricing-pending-update'
      output += '">'
      output += this.$options.filters.formatPriceWithUOM(priceData.price, stock.uomType)

      return output
    },

    getSalesRateNameForId(salesRateId) {
      let salesRate = this.salesRateList.find(salesRate => salesRate.id == salesRateId)
      if(salesRate)
        return salesRate.name
      return ''
    },

    updateStockPriceWithVariation(stock, variationPrice, priceGroupId = null, serviceRangeId = null) {
      var priceData = stock.priceData
      if(priceGroupId)
        priceData = stock.priceGroups[priceGroupId]
      if(priceGroupId && serviceRangeId)
        priceData = stock.priceGroups[priceGroupId][serviceRangeId]

      priceData.isVariation = true
      priceData.variationPrice = variationPrice
      priceData.price = priceData.variationPrice
    },
    removeStockPriceVariation(stock, priceGroupId = null, serviceRangeId = null) {
      var priceData = stock.priceData
      if(priceGroupId)
        priceData = stock.priceGroups[priceGroupId]
      if(priceGroupId && serviceRangeId)
        priceData = stock.priceGroups[priceGroupId][serviceRangeId]

      priceData.isVariation = false

      // Revert back to stored sales rate or rate variation
      let salesRate = this.salesRateList.find(salesRate => {
        if(priceData.isRateVariation)
          return salesRate.id == priceData.rateVariationSalesRateId
        return salesRate.id == priceData.salesRateId
      })
      if(!salesRate)
        return

      let adjustRateFraction = round(parseFloat(salesRate.percentAdjust) / 100.0, 2)
      priceData.calculatedPrice = round(priceData.salesPrice + (priceData.salesPrice * adjustRateFraction), 2)
      priceData.price = priceData.calculatedPrice
    },

    updateStockPriceWithVariationByStockId(stockId, variationPrice, priceGroupId = null, serviceRangeId = null) {
      let categoryLists = [this.priceListRootData.glass, this.priceListRootData.glassServices, this.priceListRootData.general, this.priceListRootData.assembly]

      categoryLists.forEach(categoryList => {
        categoryList.forEach(category => {
          var stock = category.stockList.find(stock => stock.id == stockId)
          if(stock) {
            this.updateStockPriceWithVariation(stock, variationPrice, priceGroupId, serviceRangeId)
          }
        })
      })
    },
    removeStockPriceVariationByStockId(stockId, priceGroupId = null, serviceRangeId = null) {
      let categoryLists = [this.priceListRootData.glass, this.priceListRootData.glassServices, this.priceListRootData.general, this.priceListRootData.assembly]

      categoryLists.forEach(categoryList => {
        categoryList.forEach(category => {
          var stock = category.stockList.find(stock => stock.id == stockId)
          if(stock) {
            this.removeStockPriceVariation(stock, priceGroupId, serviceRangeId)
          }
        })
      })
    },

    applySalesRateVariationByStockId(stockId, salesRateId, stockPriceGroupId, serviceRangeId) {
      let categoryLists = [this.priceListRootData.glass, this.priceListRootData.glassServices, this.priceListRootData.general, this.priceListRootData.assembly]

      let salesRate = this.salesRateList.find(salesRate => salesRate.id == salesRateId)
      if(!salesRate)
        return
      let adjustRateFraction = round(parseFloat(salesRate.percentAdjust) / 100.0, 2)

      categoryLists.forEach(categoryList => {
        categoryList.forEach(category => {
          var stock = category.stockList.find(stock => stock.id == stockId)
          if(stock) {
            var priceData = stock.priceData
            if(stockPriceGroupId)
              priceData = stock.priceGroups[stockPriceGroupId]
            if(stockPriceGroupId && serviceRangeId)
              priceData = stock.priceGroups[stockPriceGroupId][serviceRangeId]

            if(priceData && priceData.price) {
              priceData.isRateVariation = true
              priceData.rateVariationSalesRateId = salesRateId

              priceData.price = round(priceData.salesPrice + (priceData.salesPrice * adjustRateFraction), 2)
              priceData.editingPrice = priceData.price
            }
          }
        })
      })
    },
    removeSalesRateVariationByStockId(stockId, stockPriceGroupId, serviceRangeId) {
      let categoryLists = [this.priceListRootData.glass, this.priceListRootData.glassServices, this.priceListRootData.general, this.priceListRootData.assembly]

      categoryLists.forEach(categoryList => {
        categoryList.forEach(category => {
          var stock = category.stockList.find(stock => stock.id == stockId)
          if(stock) {
            var priceData = stock.priceData
            if(stockPriceGroupId)
              priceData = stock.priceGroups[stockPriceGroupId]
            if(stockPriceGroupId && serviceRangeId)
              priceData = stock.priceGroups[stockPriceGroupId][serviceRangeId]

            if(priceData && priceData.price) {

              // Revert back to stored sales rate for the category/customer
              let salesRate = this.salesRateList.find(salesRate => salesRate.id == priceData.salesRateId)
              if(!salesRate)
                return

              let adjustRateFraction = round(parseFloat(salesRate.percentAdjust) / 100.0, 2)

              priceData.isRateVariation = false
              priceData.calculatedPrice = round(priceData.salesPrice + (priceData.salesPrice * adjustRateFraction), 2)
              priceData.price = round(priceData.salesPrice + (priceData.salesPrice * adjustRateFraction), 2)
              priceData.editingPrice = priceData.price
            }
          }
        })
      })
    },


    buildStockCustomerPriceListData(customerPriceListData, stockCategoryList) {
      var outputData = {}

      // Processed Glass
      outputData.glass = stockCategoryList
      .filter(stockCategory => {
        return stockCategory.stockType == InventoryStockTypes.glass
      })
      .map(stockCategory => {
        var categoryOutput = {
          id: stockCategory.id,
          name: stockCategory.name,
          stockType: stockCategory.stockType,
          expanded: false,
          editing: false,
          submitting: false,
          displayName: this.categoryTitle(stockCategory),

          // TODO: Rate settings for category

          stockList: customerPriceListData.priceData.stockPriceData.filter(stock => stock.stockCategoryId == stockCategory.id).map(stock => {
            var outputStock = {
              id: stock.id,
              name: stock.name,
              stockType: stockCategory.stockType,
              editing: false,
              submitting: false
            }
            outputStock.priceGroups = {}

            this.$data.stockPriceGroupList.filter(priceGroup => priceGroup.isActive).forEach(priceGroup => {
              var priceInfo = null
              if(stock.priceData) {
                priceInfo = this.findPriceData(stock, priceGroup.id)
              }

              var defaultSalesRateId = null
              if(this.defaultSalesRate)
                defaultSalesRateId = this.defaultSalesRate.id
              
              outputStock.priceGroups[priceGroup.id] = {
                isRemoved: false,
                isEditing: false,
                isVariation: priceInfo ? priceInfo.isVariation : false,
                isRateVariation: priceInfo ? priceInfo.isRateVariation : false,
                price: priceInfo ? priceInfo.price : undefined,
                calculatedPrice: priceInfo ? priceInfo.price : undefined,
                prevCalculatedPrice: priceInfo ? priceInfo.prevCalculatedPrice : undefined,
                editingPrice: priceInfo ? priceInfo.editingPrice : 0,
                isSalesPendingUpdate: priceInfo ? priceInfo.isSalesPendingUpdate : false,
                originalPrice: priceInfo ? priceInfo.price : undefined,
                salesPrice: priceInfo ? priceInfo.salesPrice : undefined,
                salesRateId: priceInfo ? priceInfo.salesRateId : defaultSalesRateId,
                variationPrice: priceInfo ? priceInfo.price : 0,
                rateVariationSalesRateId: priceInfo ? priceInfo.rateVariationSalesRateId : null,
                diffDescription: priceInfo ? priceInfo.diffDescription : null
              }
            })

            return outputStock
          })
        }
        return categoryOutput
      })
      .filter(stockCategory => {
        // Filter out empty categories
        return stockCategory.stockList.length > 0
      })

      // Services
      outputData.glassServices = stockCategoryList
      .filter(stockCategory => {
        return stockCategory.stockType == InventoryStockTypes.glassService
      })
      .map(stockCategory => {
        var categoryOutput = {
          id: stockCategory.id,
          name: stockCategory.name,
          stockType: stockCategory.stockType,
          expanded: false,
          editing: false,
          submitting: false,
          displayName: this.categoryTitle(stockCategory),

          // TODO: Rate settings for category

          stockList: customerPriceListData.priceData.stockPriceData.filter(stock => stock.stockCategoryId == stockCategory.id).map(stock => {          
            var outputStock = {
              id: stock.id,
              name: stock.name,
              stockType: stockCategory.stockType,
              editing: false,
              submitting: false
            }
            outputStock.priceGroups = {}

            this.$data.stockPriceGroupList.filter(priceGroup => priceGroup.isActive).forEach(priceGroup => {
              priceGroup.servicePriceRanges.forEach(serviceRange => {

                var priceInfo = null
                if(stock.priceData) {
                  priceInfo = this.findPriceData(stock, priceGroup.id, serviceRange.id)
                }

                if(outputStock.priceGroups[priceGroup.id] === undefined)
                  outputStock.priceGroups[priceGroup.id] = {}

                var defaultSalesRateId = null
                if(this.defaultSalesRate)
                  defaultSalesRateId = this.defaultSalesRate.id

                outputStock.priceGroups[priceGroup.id][serviceRange.id] = {
                  isRemoved: false,
                  isEditing: false,
                  isVariation: priceInfo ? priceInfo.isVariation : false,
                  isRateVariation: priceInfo ? priceInfo.isRateVariation : false,
                  price: priceInfo ? priceInfo.price : undefined,
                  calculatedPrice: priceInfo ? priceInfo.price : undefined,
                  prevCalculatedPrice: priceInfo ? priceInfo.prevCalculatedPrice : undefined,
                  editingPrice: priceInfo ? priceInfo.editingPrice : 0,
                  isSalesPendingUpdate: priceInfo ? priceInfo.isSalesPendingUpdate : false,
                  originalPrice: priceInfo ? priceInfo.price : undefined,
                  salesPrice: priceInfo ? priceInfo.salesPrice : undefined,
                 salesRateId: priceInfo ? priceInfo.salesRateId : defaultSalesRateId,
                  variationPrice: priceInfo ? priceInfo.price : 0,
                  rateVariationSalesRateId: priceInfo ? priceInfo.rateVariationSalesRateId : null,
                  diffDescription: priceInfo ? priceInfo.diffDescription : null
                }
              })
            })

            return outputStock
          })
        }
        return categoryOutput
      })
      .filter(stockCategory => {
        // Filter out empty categories
        return stockCategory.stockList.length > 0
      })

      // General
      outputData.general = stockCategoryList
      .filter(stockCategory => {
        return stockCategory.stockType == InventoryStockTypes.general
      })
      .map(stockCategory => {
        var categoryOutput = {
          id: stockCategory.id,
          name: stockCategory.name,
          stockType: stockCategory.stockType,
          expanded: false,
          editing: false,
          submitting: false,
          displayName: this.categoryTitle(stockCategory),

          // TODO: Rate settings for category

          stockList: customerPriceListData.priceData.stockPriceData.filter(stock => stock.stockCategoryId == stockCategory.id).map(stock => {          
            var outputStock = {
              id: stock.id,
              name: stock.name,
              stockType: stockCategory.stockType,
              editing: false,
              submitting: false
            }

            var priceInfo = null
            if(stock.priceData) {
              priceInfo = this.findPriceData(stock)
            }

            var defaultSalesRateId = null
            if(this.defaultSalesRate)
              defaultSalesRateId = this.defaultSalesRate.id

            outputStock.priceData = {
              isRemoved: false,
              isEditing: false,
              isVariation: priceInfo ? priceInfo.isVariation : false,
              isRateVariation: priceInfo ? priceInfo.isRateVariation : false,
              price: priceInfo ? priceInfo.price : undefined,
              calculatedPrice: priceInfo ? priceInfo.price : undefined,
              prevCalculatedPrice: priceInfo ? priceInfo.prevCalculatedPrice : undefined,
              editingPrice: priceInfo ? priceInfo.editingPrice : 0,
              isSalesPendingUpdate: priceInfo ? priceInfo.isSalesPendingUpdate : false,
              originalPrice: priceInfo ? priceInfo.price : undefined,
              salesPrice: priceInfo ? priceInfo.salesPrice : undefined,
              salesRateId: priceInfo ? priceInfo.salesRateId : defaultSalesRateId,
              variationPrice: priceInfo ? priceInfo.price : 0,
              rateVariationSalesRateId: priceInfo ? priceInfo.rateVariationSalesRateId : null,
              diffDescription: priceInfo ? priceInfo.diffDescription : null
            }

            return outputStock
          })
        }
        return categoryOutput
      })
      .filter(stockCategory => {
        // Filter out empty categories
        return stockCategory.stockList.length > 0
      })

      // Assembly
      outputData.assembly = stockCategoryList
      .filter(stockCategory => {
        return stockCategory.stockType == InventoryStockTypes.assembly
      })
      .map(stockCategory => {
        var categoryOutput = {
          id: stockCategory.id,
          name: stockCategory.name,
          stockType: stockCategory.stockType,
          expanded: false,
          editing: false,
          submitting: false,
          displayName: this.categoryTitle(stockCategory),

          // TODO: Rate settings for category

          stockList: customerPriceListData.priceData.stockPriceData.filter(stock => stock.stockCategoryId == stockCategory.id).map(stock => {          
            var outputStock = {
              id: stock.id,
              name: stock.name,
              stockType: stockCategory.stockType,
              editing: false,
              submitting: false
            }

            var priceInfo = null
            if(stock.priceData) {
              priceInfo = this.findPriceData(stock)
            }

            var defaultSalesRateId = null
            if(this.defaultSalesRate)
              defaultSalesRateId = this.defaultSalesRate.id

            outputStock.priceData = {
              isRemoved: false,
              isEditing: false,
              isVariation: priceInfo ? priceInfo.isVariation : false,
              isRateVariation: priceInfo ? priceInfo.isRateVariation : false,
              price: priceInfo ? priceInfo.price : undefined,
              calculatedPrice: priceInfo ? priceInfo.price : undefined,
              prevCalculatedPrice: priceInfo ? priceInfo.prevCalculatedPrice : undefined,
              editingPrice: priceInfo ? priceInfo.editingPrice : 0,
              isSalesPendingUpdate: priceInfo ? priceInfo.isSalesPendingUpdate : false,
              originalPrice: priceInfo ? priceInfo.price : undefined,
              salesPrice: priceInfo ? priceInfo.salesPrice : undefined,
              salesRateId: priceInfo ? priceInfo.salesRateId : defaultSalesRateId,
              variationPrice: priceInfo ? priceInfo.price : 0,
              rateVariationSalesRateId: priceInfo ? priceInfo.rateVariationSalesRateId : null,
              diffDescription: priceInfo ? priceInfo.diffDescription : null
            }

            return outputStock
          })
        }
        return categoryOutput
      })
      .filter(stockCategory => {
        // Filter out empty categories
        return stockCategory.stockList.length > 0
      })

      return outputData
    },


    // Build the server price list update data list to push to the server
    // Filters available to limit the update to a specific category/stock
    buildServerPriceListUpdateListData(options = {}) {

      let filterCategoryId = options.filterCategoryId || null
      let filterStockId = options.filterStockId || null
      let updateCategory = options.updateCategory !== undefined ? options.updateCategory : true
      let updateStock = options.updateStock !== undefined ? options.updateStock : true

      // Convert stock lookup into a price update object
      // Returns null for no variations (ie, use category settings)
      let convertPriceDataFn = (stock, stockType, priceGroupId = null, serviceRangeId = null) => {
        var priceData = stock.priceData
        var priceType = StockPriceTypes.general
        if(stockType == InventoryStockTypes.assembly)
          priceType = StockPriceTypes.assembly

        if(priceGroupId) {
          priceData = stock.priceGroups[priceGroupId]
          priceType = StockPriceTypes.processedPriceGroup
        }
        if(priceGroupId && serviceRangeId) {
          priceData = stock.priceGroups[priceGroupId][serviceRangeId]
          priceType = StockPriceTypes.servicePriceRange
        }

        // If no variation, return null
        // Server will deal with removed records
        if(!priceData.isVariation && !priceData.isRateVariation)
          return null       

        // Note: Price variation always overrides rate variation
        return {
          priceType: priceType,
          overrideType: priceData.isVariation ? CustomerOverrideTypes.price : CustomerOverrideTypes.rate,
          overrideLevelType: CustomerOverrideLevelTypes.stock,
          price: priceData.price,
          stockId: stock.id,
          stockPriceGroupId: priceGroupId,
          servicePriceRangeId: serviceRangeId,
          salesRateId: priceData.isRateVariation ? priceData.rateVariationSalesRateId : null
        }
      }


      // Category settings
      var updateListData = this.customerPriceList.priceData.priceSettings
      .filter(priceSetting => {
        if(priceSetting.overrideType == CustomerOverrideTypes.rate && priceSetting.overrideLevelType == CustomerOverrideLevelTypes.category) {
          if(!updateCategory)
            return false
          if(filterCategoryId && filterCategoryId != priceSetting.stockCategoryId)
            return false
          return true
        }
        return false
      })
      .map(priceSetting => {
        return {
          priceType: priceSetting.priceType,
          overrideType: priceSetting.overrideType,
          overrideLevelType: priceSetting.overrideLevelType,
          stockCategoryId: priceSetting.stockCategoryId,
          stockPriceGroupId: priceSetting.stockPriceGroupId,
          servicePriceRangeId: priceSetting.servicePriceRangeId,
          salesRateId: priceSetting.salesRateId
        }
      })

      // Stock rate/price variations
      let categoryLists = [this.priceListRootData.glass, this.priceListRootData.glassServices, this.priceListRootData.general, this.priceListRootData.assembly]

      categoryLists.forEach(categoryList => {
        categoryList.forEach(category => {
          if(filterCategoryId && filterCategoryId != category.id)
            return

          category.stockList.forEach(stock => {
            if(filterStockId && stock.id != filterStockId)
              return
            if(!updateStock)
              return

            // Go through price groups/service ranges / general data lists
            if(stock.stockType == InventoryStockTypes.glass) {
              this.stockPriceGroupList.filter(priceGroup => priceGroup.isActive).forEach(priceGroup => {
                let priceUpdate = convertPriceDataFn(stock, category.stockType, priceGroup.id)
                if(priceUpdate)
                  updateListData.push(priceUpdate)
              })
            }

            if(stock.stockType == InventoryStockTypes.glassService) {
              this.stockPriceGroupList.filter(priceGroup => priceGroup.isActive).forEach(priceGroup => {
                priceGroup.servicePriceRanges.forEach(serviceRange => {

                  let priceUpdate = convertPriceDataFn(stock, category.stockType, priceGroup.id, serviceRange.id)
                  if(priceUpdate)
                    updateListData.push(priceUpdate)
                })
              })
            }

            if(stock.stockType == InventoryStockTypes.general) {
              let priceUpdate = convertPriceDataFn(stock, category.stockType)
              if(priceUpdate)
                updateListData.push(priceUpdate)
            }

            if(stock.stockType == InventoryStockTypes.assembly) {
              let priceUpdate = convertPriceDataFn(stock, category.stockType)
              if(priceUpdate)
                updateListData.push(priceUpdate)
            }
          })
        })
      })

      return updateListData
    }
  }
}
